import {
  SessionKey,
  sessionStorageService,
} from '@/services/SessionStorage/SessionStorageService'
import {
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react'
import { useQuery } from '@apollo/client'
import { IS_LOGGED_IN } from '@/apollo/queries'
import { SignInQueryResult } from '@/apollo/AuthRoute'
import NewFeatureModal from './NewFeatureModal'
import { FeatureConfig } from './types'
import { featureConfigs } from './featureConfig'

interface FeatureModalContextType {
  dismissFeature: (featureKey: string) => void
  isDismissed: (featureKey: string) => boolean
  getFeatureConfig: (featureKey: string) => FeatureConfig | undefined
}

export const FeatureModalContext = createContext<
  FeatureModalContextType | undefined
>(undefined)

interface FeatureModalProviderProps {
  children: ReactNode
}

export const FeatureModalProvider = ({
  children,
}: FeatureModalProviderProps) => {
  const [dismissedFeatures, setDismissedFeatures] = useState<string[]>([])
  const [activeFeatureKey, setActiveFeatureKey] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Load dismissed features from localStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorageService.getItem(
        SessionKey.DISMISSED_FEATURES_KEY
      )
      if (stored) {
        setDismissedFeatures(JSON.parse(stored))
      }
    } catch (error) {
      console.error(
        'Failed to load dismissed features from localStorage',
        error
      )
    }
  }, [])

  // Filter available features based on enabledFeatures prop
  const availableFeatures = useMemo(() => featureConfigs, [])

  // Get active features (not dismissed and not expired)
  const activeFeatures = useMemo(() => {
    const now = new Date()
    return (
      Object.entries(availableFeatures)
        .filter(([key, feature]) => {
          // Skip if dismissed
          if (dismissedFeatures.includes(key)) return false

          // Skip if expired
          if (feature.expiresAt) {
            const expiryDate = new Date(feature.expiresAt)
            if (expiryDate < now) return false
          }

          return true
        })
        // Sort by priority (higher first)
        .sort(([, a], [, b]) => (b.priority || 0) - (a.priority || 0))
    )
  }, [availableFeatures, dismissedFeatures])

  // Check if there are any features that haven't been dismissed
  const hasAvailableFeatures = activeFeatures.length > 0

  // Check if user is logged in (only if there are available features)
  const { data } = useQuery<SignInQueryResult>(IS_LOGGED_IN, {
    skip: !hasAvailableFeatures,
  })
  const isLoggedIn = Boolean(data?.isLoggedIn)

  // Update active feature when features or dismissedFeatures change
  useEffect(() => {
    // Only show features if user is logged in and there are available features
    if (isLoggedIn && hasAvailableFeatures) {
      const [topFeatureKey] = activeFeatures[0]
      setActiveFeatureKey(topFeatureKey)
      setIsModalOpen(true)
    } else {
      setActiveFeatureKey(null)
      setIsModalOpen(false)
    }
  }, [activeFeatures, isLoggedIn, hasAvailableFeatures])

  const dismissFeature = useCallback(
    (featureKey: string) => {
      setDismissedFeatures(prev => {
        const newDismissed = [...prev, featureKey]
        try {
          sessionStorageService.setItem(
            SessionKey.DISMISSED_FEATURES_KEY,
            JSON.stringify(newDismissed)
          )
        } catch (error) {
          console.error(
            'Failed to save dismissed features to localStorage',
            error
          )
        }
        return newDismissed
      })
    },
    [setDismissedFeatures]
  )

  const isDismissed = useCallback(
    (featureKey: string) => dismissedFeatures.includes(featureKey),
    [dismissedFeatures]
  )

  const getFeatureConfig = useCallback(
    (featureKey: string) => availableFeatures[featureKey],
    [availableFeatures]
  )

  const handleClose = () => {
    if (activeFeatureKey) {
      dismissFeature(activeFeatureKey)
    }
    setIsModalOpen(false)
  }

  const value = useMemo(
    () => ({
      dismissFeature,
      isDismissed,
      getFeatureConfig,
    }),
    [dismissFeature, isDismissed, getFeatureConfig]
  )

  const activeFeature = activeFeatureKey
    ? availableFeatures[activeFeatureKey]
    : null

  return (
    <FeatureModalContext.Provider value={value}>
      {children}

      {activeFeature && (
        <NewFeatureModal
          open={isModalOpen}
          onClose={handleClose}
          header={activeFeature.header}
          title={activeFeature.title}
          body={activeFeature.body}
          primaryLabel={activeFeature.primaryLabel}
          primaryAction={activeFeature.primaryAction}
          secondaryLabel={activeFeature.secondaryLabel}
          secondaryAction={activeFeature.secondaryAction}
          learnMoreLink={activeFeature.learnMoreLink}
          data-testid="feature-announcement-modal"
        />
      )}
    </FeatureModalContext.Provider>
  )
}
